import React, { useState } from 'react';
import { useBrand } from '@/context/BrandContext';
import { EditableText } from '../ui/EditableText';
import { Upload } from 'lucide-react';
import { ImageUploadModal } from '../image-upload';
import { BrandSectionLayout } from './BrandSectionLayout';

interface TableOfContentsSectionProps {
  section: any;
  isEditing: boolean;
  isGlobalDarkMode: boolean;
  editingElement: string | null;
  setEditingElement: (element: string | null) => void;
  updateSectionContent: (key: string, value: string) => void;
  updateSectionStyles: (key: string, styles: any) => void;
  navigateToSection: (sectionId: string) => void;
  prefetchSection?: (sectionId: string) => void;
  pageNumber?: number;
}

export const TableOfContentsSection: React.FC<TableOfContentsSectionProps> = ({
  section,
  isEditing,
  isGlobalDarkMode,
  editingElement,
  setEditingElement,
  updateSectionContent,
  updateSectionStyles,
  navigateToSection,
  prefetchSection,
  pageNumber = 1
}) => {
  const { state } = useBrand();
  const [showImageModal, setShowImageModal] = useState(false);

  // Get primary color from brand data
  const primaryColor = state.brandData?.colors?.primary || '#EF4444';

  const handleImageSelect = (imageUrl: string) => {
    updateSectionContent('tocImage', imageUrl);
    setShowImageModal(false);
  };

  return (
    <BrandSectionLayout
      section={section}
      isEditing={isEditing}
      isGlobalDarkMode={isGlobalDarkMode}
      editingElement={editingElement}
      setEditingElement={setEditingElement}
      updateSectionContent={updateSectionContent}
      updateSectionStyles={updateSectionStyles}
      pageNumber={pageNumber}
    >
      {/* Main flex container with full height */}
      <div className="h-full flex items-center">
        {/* Two Column Layout */}
        <div className="grid grid-cols-2 gap-16 w-full items-center">
          {/* Left Column - Image with Upload Functionality */}
          <div className="relative group w-full h-full min-h-[400px]">
            <div className="w-full h-full relative rounded-lg overflow-hidden">
              <img
                src={section.content.tocImage || '/img-placeholder-16-9.png'}
                alt="Table of Contents Visual"
                className="w-full h-full rounded-lg object-cover"
                onError={(e) => {
                  e.currentTarget.src = '/img-placeholder-16-9.png';
                }}
              />

              {/* Upload Overlay - Only visible in edit mode */}
              {isEditing && (
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center rounded-lg">
                  <div className="text-center text-white">
                    <Upload className="w-8 h-8 mx-auto mb-2" />
                    <p className="text-sm font-medium mb-2">Change Image</p>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowImageModal(true);
                      }}
                      className="px-3 py-2 bg-white/90 text-black rounded text-sm hover:bg-white transition-colors"
                    >
                      Upload New Image
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Table of Contents List */}
          <div className="flex flex-col gap-6">
            {/* Table of Contents Title */}
            <div className="mb-8">
              <EditableText
                value={section.content.listTitle || 'CONTENTS'}
                onChange={(value) => updateSectionContent('listTitle', value)}
                element="h3"
                className="text-6xl font-bold uppercase"
                placeholder="CONTENTS"
                editModeEnabled={isEditing}
                isEditing={isEditing && editingElement === 'listTitle'}
                onEditStart={() => setEditingElement('listTitle')}
                onEditEnd={() => setEditingElement(null)}
                onStyleChange={(styles) => updateSectionStyles('listTitle', styles)}
                initialStyles={{
                  fontWeight: 'bold',
                  fontSize: 60,
                  fontFamily: state.brandData?.globalTitleFont || 'Inter',
                  color: '#000000',
                  textTransform: 'uppercase',
                  ...section.content.listTitleStyles
                }}
                isGlobalDarkMode={isGlobalDarkMode}
                sectionBackgroundColor={section.customization?.backgroundColor || section.styles.backgroundColor}
              />
            </div>

            {/* Table of Contents Items */}
            {[
              { number: section.content.item1Number || '01', title: section.content.item1Title || 'Introduction', numberKey: 'item1Number', titleKey: 'item1Title', sectionId: 'introduction' },
              { number: section.content.item2Number || '02', title: section.content.item2Title || 'Logo', numberKey: 'item2Number', titleKey: 'item2Title', sectionId: 'brand-logo-cover' },
              { number: section.content.item3Number || '03', title: section.content.item3Title || 'Color', numberKey: 'item3Number', titleKey: 'item3Title', sectionId: 'color-cover' },
              { number: section.content.item4Number || '04', title: section.content.item4Title || 'Typography', numberKey: 'item4Number', titleKey: 'item4Title', sectionId: 'typography-cover' },
              { number: section.content.item5Number || '05', title: section.content.item5Title || 'Moodboard', numberKey: 'item5Number', titleKey: 'item5Title', sectionId: 'photography-cover' },
              { number: section.content.item6Number || '06', title: section.content.item6Title || 'Thank You', numberKey: 'item6Number', titleKey: 'item6Title', sectionId: 'thank-you-cover' }
            ].map((item) => (
              <div
                key={item.numberKey}
                className={`relative flex w-full items-center gap-3 ${!isEditing ? 'cursor-pointer hover:bg-gray-200 rounded-md p-2 -m-2 transition-colors' : 'p-2 -m-2'
                  }`}
                onMouseEnter={() => !isEditing && prefetchSection && prefetchSection(item.sectionId)}
                onClick={() => !isEditing && navigateToSection(item.sectionId)}
              >
                <div className="title-wrapper flex-shrink-0">
                  <EditableText
                    value={item.title}
                    onChange={(value) => updateSectionContent(item.titleKey, value)}
                    element="span"
                    className={`text-black font-bold text-xl leading-tight ${isEditing ? 'pointer-events-auto' : 'pointer-events-none'}`}
                    placeholder="Section Title"
                    editModeEnabled={isEditing}
                    isEditing={isEditing && editingElement === item.titleKey}
                    onEditStart={() => setEditingElement(item.titleKey)}
                    onEditEnd={() => setEditingElement(null)}
                    onStyleChange={(styles) => updateSectionStyles(item.titleKey, styles)}
                    initialStyles={{
                      color: '#000000',
                      fontSize: 24,
                      fontWeight: 'bold',
                      fontFamily: state.brandData?.globalTextFont || 'Inter',
                      ...section.content[`${item.titleKey}Styles`]
                    }}
                    isGlobalDarkMode={isGlobalDarkMode}
                    sectionBackgroundColor={section.customization?.backgroundColor || section.styles.backgroundColor}
                  />
                </div>
                <div className="flex-grow border-b-2 border-dotted border-gray-300" style={{ marginBottom: '4px' }}></div>
                <div className="num-wrapper flex-shrink-0">
                  <EditableText
                    value={item.number}
                    onChange={(value) => updateSectionContent(item.numberKey, value)}
                    element="span"
                    className={`font-bold text-xl ${isEditing ? 'pointer-events-auto' : 'pointer-events-none'}`}
                    style={{
                      color: primaryColor,
                      width: 'auto',
                      minWidth: 'auto',
                      textAlign: 'right',
                      display: 'inline-block'
                    }}
                    placeholder={item.number}
                    editModeEnabled={isEditing}
                    isEditing={isEditing && editingElement === item.numberKey}
                    onEditStart={() => setEditingElement(item.numberKey)}
                    onEditEnd={() => setEditingElement(null)}
                    onStyleChange={(styles) => updateSectionStyles(item.numberKey, styles)}
                    initialStyles={{
                      fontWeight: 'bold',
                      fontSize: 24,
                      color: primaryColor,
                      fontFamily: state.brandData?.globalTextFont || 'Inter',
                      ...section.content[`${item.numberKey}Styles`]
                    }}
                    isGlobalDarkMode={isGlobalDarkMode}
                    sectionBackgroundColor={section.customization?.backgroundColor || section.styles.backgroundColor}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Image Upload Modal */}
      <ImageUploadModal
        isOpen={showImageModal}
        onClose={() => setShowImageModal(false)}
        onImageSelect={handleImageSelect}
        isGlobalDarkMode={isGlobalDarkMode}
        title="Upload Table of Contents Image"
        acceptedFormats={['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg']}
        maxFileSize={10}
        showUnsplash={true}
        initialSearchQuery="content table of contents"
      />
    </BrandSectionLayout>
  );
};